import { useContext, useState, type FormEvent } from "react";
import { PickupLocationContext } from "../../../store/PickupLocationContext";
import { UserPickupContext } from "../../../store/UserPickupContext";
import { ModalContext } from "../../../store/ModalContext";
import { UsersContext } from "../../../store/UsersContext";
import { PickupContext } from "../../../store/PickupContext";
import { useNavigate } from "react-router-dom";
import {
  AccountRole,
  PaymentStatus,
  ADMIN_ASSIGNABLE_PAYMENT_STATUS_OPTIONS,
} from "../../../utils/types";
import { useTiersWithDisplayName } from "../../../hooks/useTiersWithDisplayName";
import { useAuthUser } from "../../../hooks/useAuthUser";
import { SubscriptionsContext } from "../../../store/SubscriptionsContext";
import { SeasonContext } from "../../../store/SeasonContext";
import {
  isCsaFarmPickupLocation,
  resolveCsaDeliveryDisplaySek,
} from "../../../../../constants/csaPickup";

/** Submit with a `<button type="submit" form={USER_CLIENT_FORM_ID} />` outside this `<form>`. */
export const USER_CLIENT_FORM_ID = "create-user-client-form";

export type UserClientFormProps = {
  onSubmitChange: (isSubmitting: boolean) => void;
};

const UserClientForm = ({ onSubmitChange }: UserClientFormProps) => {
  const { pickupLocations } = useContext(PickupLocationContext);
  const { openModal } = useContext(ModalContext);
  const { createPickup } = useContext(UserPickupContext);
  const { createUser, refetch } = useContext(UsersContext);
  const { refreshPickups } = useContext(PickupContext);
  const { refetch: refetchSubscriptions } = useContext(SubscriptionsContext);
  const { csaDeliveryPrice } = useContext(SeasonContext);
  const { isAuthenticated } = useAuthUser();
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedDay, setSelectedDay] = useState("");
  const [selectedSubscription, setSelectedSubscription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus | "">(
    PaymentStatus.Pending,
  );

  const validationMessage =
    "Please fill out first name, last name, email, phone, default pickup location, pickup day, and subscription type!";
  const navigate = useNavigate();

  const tiers = useTiersWithDisplayName();
  const locationOptions = pickupLocations.map((location) => (
    <option key={location._id} value={location._id}>
      {location.name}
    </option>
  ));

  const selectedPickupLocation = pickupLocations.find(
    (location) => location._id === selectedLocation,
  );
  const days = selectedPickupLocation?.days;
  const dayOptions = days?.map((day) => (
    <option key={day.day}>{day.day}</option>
  ));
  const pickupLocationId = selectedPickupLocation?._id;

  const deliveryChargeSek = selectedLocation
    ? resolveCsaDeliveryDisplaySek(selectedLocation, csaDeliveryPrice)
    : null;
  const selectedTier = tiers.find((tier) => tier.name === selectedSubscription);
  const needsPaymentCheckout =
    paymentStatus === PaymentStatus.Pending ||
    paymentStatus === PaymentStatus.Failed ||
    paymentStatus === PaymentStatus.Expired;

  function getTierId() {
    const tier = tiers.find((sub) => sub.name === selectedSubscription);
    return tier?.id;
  }

  function clientFormIsValid(
    data: Record<string, FormDataEntryValue>,
  ): boolean {
    const first = String(data.firstName ?? "").trim();
    const last = String(data.lastName ?? "").trim();
    const email = String(data.email ?? "").trim();
    const phone = String(data.phone ?? "").trim();
    return Boolean(
      first &&
      last &&
      email &&
      phone &&
      selectedLocation &&
      selectedDay &&
      selectedSubscription &&
      getTierId(),
    );
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isSubmitting) return;

    const fd = new FormData(event.currentTarget);
    const data = Object.fromEntries(fd.entries());

    if (!clientFormIsValid(data)) {
      setFormError(validationMessage);
      return;
    }

    setFormError(null);

    const tierId = getTierId()!;

    if (!selectedTier?.isAvailable) {
      setFormError("This tier is full. Please choose another tier.");
      return;
    }

    if (!isAuthenticated) {
      openModal({
        body: "You must be logged in (MongoDB) to create users.",
        showCloseButton: true,
      });
      return;
    }
    if (paymentStatus === "") {
      setFormError("Please select a payment status.");
      return;
    }

    setIsSubmitting(true);
    onSubmitChange(true);
    try {
      const result = await createUser({
        role: AccountRole.Social,
        firstName: data.firstName as string,
        lastName: data.lastName as string,
        email: data.email as string,
        phoneNumber: data.phone as string,
        subscriptionTierId: tierId,
      });
      openModal({
        title: "Success!",
        body: "The account has been created and an email has been sent.",
        showCloseButton: true,
      });

      if (pickupLocationId && result.userId && paymentStatus) {
        await createPickup({
          userId: result.userId,
          pickupLocationId,
          pickupDay: selectedDay,
          subscriptionTierId: tierId,
          subscriptionPaymentStatus: paymentStatus,
        });
      }
      await refetch();
      await refreshPickups();
      refetchSubscriptions();
      navigate("/dashboard/admin/manage-users");
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Failed to create user";
      openModal({ body: msg, showCloseButton: true });
    } finally {
      setIsSubmitting(false);
      onSubmitChange(false);
    }
  }

  const subscriptionOptions = tiers.map((tier) => {
    if (tier.isAvailable) {
      return <option key={tier.id}>{tier.name}</option>;
    }
    return null;
  });

  return (
    <form
      id={USER_CLIENT_FORM_ID}
      noValidate
      onSubmit={handleSubmit}
      onInput={() => setFormError(null)}
      className="form"
    >
      <div className="form-fields-container">
        <div className="modal-form-input-pair">
          <label htmlFor="first-name" className="sr-only">
            First Name
          </label>
          <input
            type="text"
            name="firstName"
            id="first-name"
            placeholder="First Name"
          />
          <label htmlFor="last-name" className="sr-only">
            Last Name
          </label>
          <input
            type="text"
            name="lastName"
            id="last-name"
            placeholder="Last Name"
          />
        </div>
        <div className="modal-form-input-pair">
          <label htmlFor="email" className="sr-only">
            Email
          </label>
          <input type="email" name="email" id="email" placeholder="Email" />
          <label htmlFor="phone" className="sr-only">
            Phone Number
          </label>
          <input type="phone" name="phone" id="phone" placeholder="Phone" />
        </div>
        <div className=".dropdown-long-wrapper">
          <label htmlFor="selected-location" className="sr-only">
            Default Pickup Location
          </label>
          <select
            className="dropdown create-dropdown-long"
            value={selectedLocation}
            onChange={(e) => {
              setSelectedLocation(e.target.value);
              setFormError(null);
            }}
          >
            {!selectedLocation && (
              <option value="">Default Pickup Location</option>
            )}
            {locationOptions}
          </select>
        </div>
        <div className="dropdown-long-wrapper">
          <label htmlFor="selected-day" className="sr-only">
            Default Pickup Day
          </label>
          <select
            className="dropdown create-dropdown-long"
            value={selectedDay}
            onChange={(e) => {
              setSelectedDay(e.target.value);
              setFormError(null);
            }}
          >
            {!selectedDay && <option value="">Default Pickup Day</option>}
            {dayOptions}
          </select>
        </div>
        <div className="dropdown-long-wrapper">
          <label htmlFor="selected-subscription" className="sr-only">
            Subscription type
          </label>
          <select
            className="dropdown create-dropdown-long"
            value={selectedSubscription}
            onChange={(e) => {
              setSelectedSubscription(e.target.value);
              setFormError(null);
            }}
          >
            {!selectedSubscription && (
              <option value="">Default Subscription Type</option>
            )}
            {subscriptionOptions}
          </select>
        </div>{" "}
        <div className="dropdown-long-wrapper">
          <label htmlFor="payment-status" className="sr-only">
            Payment Status
          </label>
          <select
            id="payment-status"
            className="dropdown create-dropdown-long"
            value={paymentStatus}
            onChange={(e) => {
              setPaymentStatus(e.target.value as PaymentStatus);
              setFormError(null);
            }}
          >
            {!paymentStatus && <option value="">Payment Status</option>}
            {ADMIN_ASSIGNABLE_PAYMENT_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedLocation &&
        deliveryChargeSek !== null &&
        selectedTier &&
        needsPaymentCheckout && (
          <p>
            Payment link includes subscription
            {isCsaFarmPickupLocation(selectedLocation) ? "." : " + delivery."}
          </p>
        )}
      {paymentStatus === PaymentStatus.Paid && (
        <p>
          Paid: no Stripe checkout is sent. Collect subscription and delivery
          fees manually if needed.
        </p>
      )}

      {formError && (
        <p className="pickup-form-validation-msg" role="alert">
          {formError}
        </p>
      )}
    </form>
  );
};

export default UserClientForm;
